/**
 * Key roles — every canonical role this loan needs, filled or not.
 *
 * Three bands inside one card:
 *
 *   1. summary   "3 of 7 key roles filled" over a SEGMENTED meter — one segment
 *                per role, green when filled and hatched amber when not, so the
 *                bar reads as "which roles", not just "how much". Each segment
 *                scrolls its own card into view.
 *   2. missing   the unfilled roles as chips; clicking one opens the assign
 *                dialog on that role.
 *   3. slots     the role cards themselves (KeyRoleSlotCard).
 *
 * "Show missing only" filters the grid down to the gaps — the fastest path
 * through a file that is nearly complete.
 *
 * General Contractor is deliberately NOT a key role: GCs live on their own
 * `generalContractors[]` collection with UW confirmations and experience, and
 * they get the section below rather than a slot up here.
 */
import { Box, Switch } from "@mui/material";
import PropTypes from "prop-types";
import { memo, useCallback, useMemo, useState } from "react";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import LoanSectionCard from "../../../shared/tabLayout/LoanSectionCard";
import { TAB_TOKENS as T } from "../../../shared/tabLayout/tabTokens";
import { formSx } from "../../../../../theme/formStyles";
import { CONTACTS_SECTION_ANCHOR_SX } from "../contactsSections";
import KeyRoleSlotCard from "./KeyRoleSlotCard";
import { MISSING } from "./contactsTokens";

/** id of the slot card, so the meter and the chips can scroll to one. */
const slotId = (role) => `key-role-${role.toLowerCase().replace(/\W+/g, "-")}`;

const scrollToRole = (role) => {
  const el = document.getElementById(slotId(role));
  if (!el) return;
  el.scrollIntoView({ block: "nearest", behavior: "smooth" });
};

/** One segment per role — filled is solid green, missing is hatched amber. */
const RoleMeter = memo(({ slots, onJump }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: `repeat(${slots.length}, 1fr)`,
      gap: "4px",
      marginTop: "10px",
    }}
  >
    {slots.map(({ role, contact }) => (
      <Box
        key={role}
        component="button"
        type="button"
        onClick={() => onJump(role)}
        title={`${role}: ${contact ? "Filled" : "Not assigned"}`}
        aria-label={`${role}: ${contact ? "Filled" : "Not assigned"}`}
        sx={{
          height: 8,
          border: 0,
          padding: 0,
          borderRadius: "4px",
          cursor: "pointer",
          background: contact
            ? T.color.okInk
            : `repeating-linear-gradient(135deg, ${MISSING.hatchBase} 0 4px, ${MISSING.hatchStripe} 4px 6px)`,
          "&:hover": { filter: "brightness(0.92)" },
          "&:focus-visible": {
            outline: `2px solid ${T.color.brand}`,
            outlineOffset: "2px",
          },
        }}
      />
    ))}
  </Box>
));

const MissingChip = ({ role, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      font: "inherit",
      fontSize: "12.5px",
      fontWeight: 500,
      lineHeight: 1.4,
      padding: "3px 11px",
      borderRadius: "999px",
      cursor: "pointer",
      color: MISSING.ink,
      background: MISSING.chipFill,
      border: `1px solid ${MISSING.line}`,
      "&:hover": { borderColor: MISSING.lineHover },
      "&:focus-visible": {
        outline: `2px solid ${T.color.brand}`,
        outlineOffset: "2px",
      },
    }}
  >
    {role}
  </Box>
);

const KeyRolesSection = ({
  id,
  slots,
  editingId,
  onOpenContact,
  onAssignRole,
  onRemoveContact,
}) => {
  const [missingOnly, setMissingOnly] = useState(false);

  const filled = useMemo(() => slots.filter((s) => s.contact).length, [slots]);
  const missing = useMemo(
    () => slots.filter((s) => !s.contact).map((s) => s.role),
    [slots],
  );
  const complete = missing.length === 0;

  const visible = useMemo(
    () => (missingOnly ? slots.filter((s) => !s.contact) : slots),
    [slots, missingOnly],
  );

  const jump = useCallback((role) => scrollToRole(role), []);

  return (
    <LoanSectionCard
      id={id}
      sx={CONTACTS_SECTION_ANCHOR_SX}
      title={
        // The filled count rides with the title — the same n/total pill the
        // panel forms put on a section (teal once complete).
        <Flx ac component="span" sx={{ gap: 1 }}>
          Key roles
          <Box
            component="span"
            aria-live="polite"
            aria-label={`${filled} of ${slots.length} key roles filled`}
            sx={formSx.sectionCount(complete)}
          >
            {filled}/{slots.length}
          </Box>
        </Flx>
      }
      collapsible
      chevronStart
      subTitle="Every role this loan needs and who's filling it. Assign an empty role to add a contact with that role already selected."
      controls={
        <Flx
          ac
          component="label"
          sx={{ gap: "6px", cursor: "pointer", userSelect: "none" }}
        >
          <Switch
            size="small"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
            inputProps={{ "aria-label": "Show missing only" }}
          />
          <Txt sx={{ fontSize: "12.5px", color: T.color.ink3 }}>
            Show missing only
          </Txt>
        </Flx>
      }
      disableBodyPadding
    >
      {/* 1 — meter + missing chips (the count lives in the header) */}
      {/* 6px + the meter's own 10px top margin = 16px under the header rule */}
      <Box sx={{ padding: "6px 16px 0" }}>
        <RoleMeter slots={slots} onJump={jump} />

        {missing.length > 0 && (
          <Flx ac wrap sx={{ gap: "6px", my: "10px" }}>
            <Txt sx={{ fontSize: "12.5px", color: T.color.ink3 }}>Missing:</Txt>
            {missing.map((role) => (
              <MissingChip
                key={role}
                role={role}
                onClick={() => onAssignRole(role)}
              />
            ))}
          </Flx>
        )}
      </Box>

      {/* 2 — the slots */}
      {visible.length === 0 ? (
        <Box
          sx={{
            margin: "14px 16px 16px",
            padding: "16px",
            textAlign: "center",
            borderRadius: T.radius.sm,
            background: T.color.okSoft,
            color: T.color.okInk,
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          Nothing missing. Every key role has a contact.
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(295px, 1fr))",
            gap: "10px",
            padding: "14px 16px 16px",
            [T.bp.twoUp]: { gridTemplateColumns: "1fr" },
          }}
        >
          {visible.map(({ role, contact, extraCount }) => (
            // `display: flex` on the grid item so the card fills the row's
            // height: an empty slot carries two lines to a filled slot's three,
            // and left to size itself it sits visibly short of its neighbours.
            <Box
              key={role}
              id={slotId(role)}
              sx={{ display: "flex", scrollMarginTop: "90px" }}
            >
              <KeyRoleSlotCard
                role={role}
                contact={contact}
                extraCount={extraCount}
                selected={Boolean(contact) && contact._id === editingId}
                onOpen={() => onOpenContact(contact)}
                onAssign={() => onAssignRole(role, contact)}
                onRemove={() => onRemoveContact(contact, role)}
              />
            </Box>
          ))}
        </Box>
      )}
    </LoanSectionCard>
  );
};

RoleMeter.displayName = "RoleMeter";

KeyRolesSection.propTypes = {
  id: PropTypes.string,
  /** [{ role, contact|null, extraCount }] in canonical order. */
  slots: PropTypes.array.isRequired,
  editingId: PropTypes.string,
  onOpenContact: PropTypes.func.isRequired,
  /** (role, currentContact|null) — opens the assign / change dialog. */
  onAssignRole: PropTypes.func.isRequired,
  /** (contact, role) — asks to remove; the caller confirms. */
  onRemoveContact: PropTypes.func.isRequired,
};

export default memo(KeyRolesSection);
